import React, { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LINES = [
  { label: 'Scout', text: 'Scouting target companies...' },
  { label: 'Outreach', text: 'Writing outreach email...' },
  { label: 'Objections', text: 'Building battle card...' },
];

export default function ProcessingScreen({ isDemo, brief, onResultsReady, fallbackData }) {
  const [visibleLines, setVisibleLines] = useState([]);
  const [doneLines, setDoneLines] = useState([]);
  const [progress, setProgress] = useState(0);
  const doneRef = useRef(false);

  useEffect(() => {
    STATUS_LINES.forEach((_, i) => {
      setTimeout(() => setVisibleLines((prev) => [...prev, i]), i * 800);
      setTimeout(() => setDoneLines((prev) => [...prev, i]), i * 800 + 600);
    });

    const duration = isDemo ? 1500 : 3000;
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min((elapsed / duration) * 100, 99));
    }, 50);

    const finish = (data) => {
      if (doneRef.current) return;
      doneRef.current = true;
      clearInterval(progressInterval);
      setProgress(100);
      setTimeout(() => onResultsReady(data), 150);
    };

    if (isDemo) {
      const t = setTimeout(() => finish(fallbackData), 1500);
      return () => { clearTimeout(t); clearInterval(progressInterval); };
    } else {
      const apiTimeout = setTimeout(() => finish(fallbackData), 8000);
      fetch(`${BACKEND_URL}/api/gtm/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brief }),
      })
        .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
        .then((data) => { clearTimeout(apiTimeout); finish(data); })
        .catch(() => { clearTimeout(apiTimeout); finish(fallbackData); });

      return () => { clearTimeout(apiTimeout); clearInterval(progressInterval); };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-canvas flex flex-col">

      {/* Ink nav strip */}
      <div
        className="flex items-center justify-between px-8 h-16"
        style={{ background: '#25282b' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="inline-flex items-center justify-center flex-shrink-0"
            style={{ width: 28, height: 28, background: '#e60000', borderRadius: '6px' }}
            aria-label="Marketers"
          >
            <svg width="17" height="13" viewBox="0 0 20 16" fill="none">
              <rect x="0" y="4" width="4" height="8" rx="1" fill="white" />
              <path d="M4 3L19 0V16L4 13V3Z" fill="white" />
            </svg>
          </div>
          <span className="text-white font-bold text-lg tracking-tight">Marketers</span>
        </div>
      </div>

      {/* Centered content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <p
          className="text-body-text mb-8"
          style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase' }}
        >
          Running your agents
        </p>

        {/* Status card */}
        <div
          className="w-80 mb-8"
          style={{ border: '1.5px solid #25282b', borderRadius: '6px', overflow: 'hidden' }}
          data-testid="processing-status"
        >
          {/* Card header band */}
          <div
            className="px-5 py-3"
            style={{ background: '#25282b' }}
          >
            <p className="text-white text-sm font-bold tracking-wide uppercase">Agents</p>
          </div>

          {/* Status lines */}
          <div className="bg-canvas px-5 py-4 space-y-4">
            {STATUS_LINES.map((line, i) => (
              <div
                key={i}
                data-testid={`status-line-${i}`}
                className="flex items-center gap-3"
                style={{
                  opacity: visibleLines.includes(i) ? 1 : 0,
                  transform: visibleLines.includes(i) ? 'none' : 'translateY(6px)',
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                }}
              >
                {/* Status dot */}
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0 transition-colors duration-300"
                  style={{
                    background: doneLines.includes(i) ? '#e60000' : '#bebebe',
                  }}
                />
                <span className="flex-1 text-sm text-ink">{line.text}</span>
                <span
                  className="text-xs font-bold transition-colors duration-300"
                  style={{
                    color: doneLines.includes(i) ? '#e60000' : '#bebebe',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {doneLines.includes(i) ? 'Done' : '...'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-80">
          <div
            className="h-0.5 overflow-hidden"
            style={{ background: '#f2f2f2' }}
          >
            <div
              className="h-full bg-primary"
              style={{ width: `${progress}%`, transition: 'width 60ms linear' }}
              data-testid="progress-bar"
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-body-text text-xs uppercase tracking-wider font-semibold">Processing</span>
            <span className="text-body-text text-xs font-semibold">{Math.round(progress)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
